import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { LibraryModule } from '../shared/lib/lib.module';
import { LocationAdminService } from '../core/services/location-admin.service';
import { GoogleMapsLoaderService } from '../core/services/google-maps-loader.service';
import { AdminLocationItem } from '../core/models/location-admin.model';

type LocationFormMode = 'create' | 'edit';

type AddressPredictionOption = {
  label: string;
  value: string;
  id?: string | null;
};

@Component({
  selector: 'location-form-component, LocationFormComponent',
  standalone: true,
  templateUrl: './location-form.component.html',
  styleUrls: ['./location-form.component.css'],
  imports: [CommonModule, FormsModule, LibraryModule, RouterModule],
})
export class LocationFormComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly locationAdminService = inject(LocationAdminService);
  private readonly googleMapsLoader = inject(GoogleMapsLoaderService);

  mode: LocationFormMode = 'create';
  locationId = '';
  isLoading = true;
  isSaving = false;
  errorMessage: string | null = null;
  locationDetail: AdminLocationItem | null = null;

  locationName = '';
  streetAddress = '';
  city = '';
  state = '';
  zip = '';
  isActive = true;

  addressOptions: AddressPredictionOption[] = [];
  addressSearchLoading = false;
  showAddressSuggestions = false;

  private autocompleteService: google.maps.places.AutocompleteService | null = null;
  private readonly addressSearchSubject = new Subject<string>();
  private addressSearchSubscription?: Subscription;

  ngOnInit(): void {
    this.addressSearchSubscription = this.addressSearchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((query) => this.performAddressSearch(query));

    void this.googleMapsLoader.load().then(() => {
      this.autocompleteService = this.googleMapsLoader.getAutocompleteService();
    }).catch(() => {
      // Address can still be typed manually if Maps fails to load.
    });

    const url = this.route.snapshot.url.map((segment) => segment.path);
    const locationId = this.route.snapshot.paramMap.get('locationId') ?? '';

    if (url.includes('create')) {
      this.mode = 'create';
      this.isLoading = false;
      return;
    }

    if (url.includes('edit') && locationId) {
      this.mode = 'edit';
      this.locationId = locationId;
      this.loadLocation();
      return;
    }

    void this.router.navigate(['/location-management']);
  }

  ngOnDestroy(): void {
    this.addressSearchSubscription?.unsubscribe();
    this.addressSearchSubject.complete();
  }

  get pageTitle(): string {
    return this.mode === 'create' ? 'Create Location' : 'Edit Location';
  }

  onBack(): void {
    void this.router.navigate(['/location-management']);
  }

  onStreetAddressInput(value: string): void {
    const query = String(value ?? '');
    this.streetAddress = query;
    this.showAddressSuggestions = true;

    // Clearing street address should also clear dependent city / state / zip.
    if (!query.trim()) {
      this.clearDependentAddressFields();
      this.addressOptions = [];
      this.addressSearchLoading = false;
      if (this.errorMessage === 'Zip is required.' || this.errorMessage === 'City is required.' || this.errorMessage === 'State is required.') {
        this.errorMessage = null;
      }
      return;
    }

    if (query.trim().length < 2) {
      this.addressOptions = [];
      this.addressSearchLoading = false;
      return;
    }

    this.addressSearchLoading = true;
    this.addressSearchSubject.next(query.trim());
  }

  /** Reset city/state/zip when the lookup street line is removed. */
  private clearDependentAddressFields(): void {
    this.city = '';
    this.state = '';
    this.zip = '';
  }

  onStreetAddressBlur(): void {
    // Delay so suggestion mousedown can run before the list closes.
    setTimeout(() => {
      this.showAddressSuggestions = false;
    }, 150);
  }

  onStreetAddressSelected(option: AddressPredictionOption): void {
    const placeId = String(option?.id ?? '').trim() || null;
    const fallbackValue = String(option?.value ?? option?.label ?? '').trim();
    this.showAddressSuggestions = false;
    this.addressOptions = [];

    if (!placeId) {
      if (fallbackValue) {
        this.streetAddress = fallbackValue;
      }
      return;
    }

    if (typeof google === 'undefined' || !google.maps?.places) {
      this.streetAddress = fallbackValue;
      return;
    }

    const service = new google.maps.places.PlacesService(document.createElement('div'));
    service.getDetails(
      {
        placeId,
        fields: ['formatted_address', 'address_components'],
      },
      (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          this.applyPlaceDetails(place);
        } else if (fallbackValue) {
          this.streetAddress = fallbackValue;
        }
      },
    );
  }

  onSubmit(): void {
    if (this.isSaving) {
      return;
    }

    this.errorMessage = null;

    if (!this.locationName.trim()) {
      this.errorMessage = 'Location name is required.';
      return;
    }

    if (!this.streetAddress.trim()) {
      this.errorMessage = 'Street address is required.';
      return;
    }

    if (!this.city.trim()) {
      this.errorMessage = 'City is required.';
      return;
    }

    if (!this.state.trim()) {
      this.errorMessage = 'State is required.';
      return;
    }

    if (!this.zip.trim()) {
      this.errorMessage = 'Zip is required.';
      return;
    }

    const addressLine = this.buildAddressLine();
    this.isSaving = true;

    if (this.mode === 'create') {
      this.locationAdminService
        .createLocation({
          locationName: this.locationName.trim(),
          addressLine,
          isActive: this.isActive,
        })
        .subscribe({
          next: () => {
            this.isSaving = false;
            void this.router.navigate(['/location-management']);
          },
          error: (err) => {
            this.errorMessage = err?.error?.message ?? 'Unable to create location.';
            this.isSaving = false;
          },
        });
      return;
    }

    this.locationAdminService
      .updateLocation(this.locationId, {
        locationName: this.locationName.trim(),
        addressLine,
        isActive: this.isActive,
      })
      .subscribe({
        next: () => {
          this.isSaving = false;
          void this.router.navigate(['/location-management']);
        },
        error: (err) => {
          this.errorMessage = err?.error?.message ?? 'Unable to update location.';
          this.isSaving = false;
        },
      });
  }

  private loadLocation(): void {
    this.isLoading = true;
    this.errorMessage = null;

    this.locationAdminService.getLocation(this.locationId).subscribe({
      next: (response) => {
        this.locationDetail = response.location;
        this.locationName = response.location.locationName ?? '';
        this.applyExistingAddressLine(response.location.addressLine ?? '');
        this.isActive = response.location.isActive ?? true;
        this.isLoading = false;
      },
      error: (err) => {
        this.errorMessage = err?.error?.message ?? 'Unable to load location.';
        this.isLoading = false;
      },
    });
  }

  private performAddressSearch(query: string): void {
    const trimmedQuery = query?.trim() || '';
    if (!trimmedQuery || trimmedQuery.length < 2) {
      this.addressOptions = [];
      this.addressSearchLoading = false;
      return;
    }

    if (!this.autocompleteService) {
      if (this.googleMapsLoader.isLoaded()) {
        this.autocompleteService = this.googleMapsLoader.getAutocompleteService();
      } else {
        void this.googleMapsLoader.load().then(() => {
          this.autocompleteService = this.googleMapsLoader.getAutocompleteService();
          if (this.autocompleteService) {
            this.performAddressSearch(trimmedQuery);
          } else {
            this.addressSearchLoading = false;
          }
        }).catch(() => {
          this.addressSearchLoading = false;
        });
        return;
      }
    }

    if (!this.autocompleteService) {
      this.addressSearchLoading = false;
      return;
    }

    this.addressSearchLoading = true;
    this.autocompleteService.getPlacePredictions(
      {
        input: trimmedQuery,
        types: ['address'],
        componentRestrictions: { country: 'us' },
      },
      (predictions, status) => {
        this.addressSearchLoading = false;
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions?.length) {
          this.addressOptions = predictions.map((prediction) => ({
            label: prediction.description,
            value: prediction.description,
            id: prediction.place_id,
          }));
          this.showAddressSuggestions = true;
          return;
        }
        this.addressOptions = [];
      },
    );
  }

  private applyPlaceDetails(place: google.maps.places.PlaceResult): void {
    const components = place.address_components ?? [];
    let streetNumber = '';
    let route = '';
    let city = '';
    let state = '';
    let zip = '';

    for (const component of components) {
      const types = component.types ?? [];
      if (types.includes('street_number')) {
        streetNumber = component.long_name;
      }
      if (types.includes('route')) {
        route = component.long_name;
      }
      if (types.includes('locality')) {
        city = component.long_name;
      }
      if (!city && types.includes('sublocality_level_1')) {
        city = component.long_name;
      }
      if (!city && types.includes('postal_town')) {
        city = component.long_name;
      }
      if (types.includes('administrative_area_level_1')) {
        state = component.short_name;
      }
      if (types.includes('postal_code')) {
        zip = component.long_name;
      }
    }

    const streetFromParts = [streetNumber, route].filter(Boolean).join(' ').trim();
    this.streetAddress =
      streetFromParts ||
      place.formatted_address?.split(',')[0]?.trim() ||
      place.formatted_address ||
      this.streetAddress;
    this.city = city || this.city;
    this.state = state || this.state;
    this.zip = zip || this.zip;

    this.addressOptions = [];
    this.showAddressSuggestions = false;
  }

  private applyExistingAddressLine(addressLine: string): void {
    const raw = String(addressLine ?? '').trim();
    if (!raw) {
      this.streetAddress = '';
      this.city = '';
      this.state = '';
      this.zip = '';
      return;
    }

    const parts = raw.split(',').map((part) => part.trim()).filter(Boolean);
    if (parts.length >= 3) {
      this.streetAddress = parts[0];
      this.city = parts[1];
      const stateZip = parts.slice(2).join(' ').trim();
      const match = stateZip.match(/^([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);
      if (match) {
        this.state = match[1].toUpperCase();
        this.zip = match[2];
        return;
      }
      const tokens = stateZip.split(/\s+/).filter(Boolean);
      if (tokens.length >= 2 && /^\d{5}/.test(tokens[tokens.length - 1])) {
        this.zip = tokens[tokens.length - 1];
        this.state = tokens.slice(0, -1).join(' ');
        return;
      }
      this.state = stateZip;
      this.zip = '';
      return;
    }

    this.streetAddress = raw;
    this.city = '';
    this.state = '';
    this.zip = '';
  }

  private buildAddressLine(): string {
    const street = this.streetAddress.trim();
    const city = this.city.trim();
    const state = this.state.trim();
    const zip = this.zip.trim();
    return `${street}, ${city}, ${state} ${zip}`.replace(/\s+/g, ' ').trim();
  }
}
